import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NotificationService, type NotificationPageSize } from '@/services/notification/notificationService';
import { Notification } from '@/types/notification';
import { useContext, useEffect, useRef, useState } from 'react';
import { AuthContext } from '@/context/AuthContext';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useHasUnreadNotifications } from '@/hooks/useHasUnreadNotifications';
import { playAlertChime } from '@/lib/alerts/alertFeedback';

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = NotificationService.DEFAULT_PAGE_SIZE;

export const useNotifications = (
  initialPageSize: number = DEFAULT_PAGE_SIZE,
) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const user = authContext?.user;
  const currentUserId = String(user?.id || "");
  const previousUnreadRef = useRef<boolean | null>(null);
  const isInitialLoadRef = useRef<boolean>(true);
  const previousUserIdRef = useRef<string>("");

  const [page, setPage] = useState(DEFAULT_PAGE);
  const [pageSize, setPageSize] = useState<number>(initialPageSize);

  // Reset initial load flag when userId changes
  useEffect(() => {
    if (currentUserId && currentUserId !== previousUserIdRef.current) {
      previousUnreadRef.current = null;
      isInitialLoadRef.current = true;
      previousUserIdRef.current = currentUserId;
      setPage(DEFAULT_PAGE);
      queryClient.removeQueries({ queryKey: ["notifications"], exact: false });
      queryClient.removeQueries({
        queryKey: ["hasUnreadNotifications"],
        exact: false,
      });
    }
  }, [currentUserId, queryClient]);

  // Get notifications with pagination (poll with same cadence as hasUnread)
  const notificationsQuery = useQuery({
    queryKey: ["notifications", currentUserId, page, pageSize],
    queryFn: () =>
      NotificationService.getAllNotifications(currentUserId, page, pageSize),
    enabled: !!currentUserId,
    staleTime: 30 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchInterval: 30 * 1000,
    refetchOnWindowFocus: true,
  });

  const notifications = notificationsQuery.data?.data ?? [];
  const totalNotifications = notificationsQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalNotifications / pageSize));

  const {
    hasUnreadNotifications,
    hasUnreadData,
    isLoadingHasUnread: hasUnreadLoading,
    hasUnreadError,
    refetchHasUnread,
  } = useHasUnreadNotifications();

  // Play sound when new unread notifications are detected
  useEffect(() => {
    // Skip if query is still loading or no user
    if (
      !currentUserId ||
      hasUnreadLoading ||
      hasUnreadData === undefined
    ) {
      return;
    }

    const currentUnread = hasUnreadData ?? false;
    const previousUnread = previousUnreadRef.current;

    // On initial load, just set the ref and skip sound
    if (isInitialLoadRef.current) {
      previousUnreadRef.current = currentUnread;
      isInitialLoadRef.current = false;
      return;
    }

    // Only play sound and show toast when transitioning from no unread (false) to having unread (true)
    // This means previousUnread must be explicitly false (not null)
    if (currentUnread && previousUnread === false) {
      // Keep dashboard preview / notifications page in sync immediately
      void queryClient.invalidateQueries({
        queryKey: ["notifications", currentUserId],
      });
      // Generic alert chime (outbid / win use their own mp3 paths elsewhere).
      playAlertChime();
      // Stable id replaces any previous "new notifications" toast instead of stacking
      toast.info("Du har nya notifikationer!", {
        id: "bilenia-new-notifications",
        duration: 4000,
        action: {
          label: "Visa",
          onClick: () => navigate("/notifications"),
        },
      });
    }

    // Always update the ref for next comparison
    previousUnreadRef.current = currentUnread;
  }, [hasUnreadData, hasUnreadLoading, currentUserId, queryClient, navigate]);

  // Mark notification as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: ({
      notificationId,
      userId,
    }: {
      notificationId: number;
      userId: string;
    }) => NotificationService.markAsRead(notificationId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["notifications", currentUserId],
      });
      queryClient.invalidateQueries({
        queryKey: ["hasUnreadNotifications"],
      });
    },
  });

  // Delete notification mutation
  const deleteNotificationMutation = useMutation({
    mutationFn: ({
      notificationId,
      userId,
    }: {
      notificationId: number;
      userId: string;
    }) => NotificationService.deleteNotification(notificationId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["notifications", currentUserId],
      });
      queryClient.invalidateQueries({
        queryKey: ["hasUnreadNotifications"],
      });
    },
  });

  // Mark all notifications as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: (userId: string) => NotificationService.markAllAsRead(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["notifications", currentUserId],
      });
      queryClient.invalidateQueries({
        queryKey: ["hasUnreadNotifications", currentUserId],
      });
    },
  });

  // Delete all notifications mutation
  const deleteAllNotificationsMutation = useMutation({
    mutationFn: (userId: string) =>
      NotificationService.deleteAllNotifications(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["notifications", currentUserId],
      });
      queryClient.invalidateQueries({
        queryKey: ["hasUnreadNotifications", currentUserId],
      });
    },
  });

  const setPageSizeOption = (size: NotificationPageSize) => {
    setPageSize(size);
    setPage(DEFAULT_PAGE);
  };

  return {
    // data
    notifications,
    totalNotifications,
    totalPages,
    page,
    pageSize,
    setPage,
    setPageSize: setPageSizeOption,
    pageSizeOptions: NotificationService.PAGE_SIZE_OPTIONS,
    isLoadingNotifications: notificationsQuery.isLoading,
    notificationsError: notificationsQuery.error,
    refetchNotifications: notificationsQuery.refetch,

    // unread status
    hasUnreadNotifications: hasUnreadNotifications || false,
    isLoadingHasUnread: hasUnreadLoading,
    hasUnreadError,
    refetchHasUnread,

    // debug
    debug: {
      data: notificationsQuery.data,
      isLoading: notificationsQuery.isLoading,
      error: notificationsQuery.error,
      length: notifications.length,
    },

    // mutations
    markAsRead: markAsReadMutation.mutate,
    markAsReadAsync: markAsReadMutation.mutateAsync,
    isMarkingAsRead: markAsReadMutation.isPending,
    markAsReadError: markAsReadMutation.error,
    markAsReadSuccess: markAsReadMutation.isSuccess,
    resetMarkAsRead: markAsReadMutation.reset,

    deleteNotification: deleteNotificationMutation.mutate,
    deleteNotificationAsync: deleteNotificationMutation.mutateAsync,
    isDeletingNotification: deleteNotificationMutation.isPending,
    deleteNotificationError: deleteNotificationMutation.error,
    deleteNotificationSuccess: deleteNotificationMutation.isSuccess,
    resetDeleteNotification: deleteNotificationMutation.reset,

    markAllAsRead: markAllAsReadMutation.mutate,
    markAllAsReadAsync: markAllAsReadMutation.mutateAsync,
    isMarkingAllAsRead: markAllAsReadMutation.isPending,
    markAllAsReadError: markAllAsReadMutation.error,
    markAllAsReadSuccess: markAllAsReadMutation.isSuccess,
    resetMarkAllAsRead: markAllAsReadMutation.reset,

    deleteAllNotifications: deleteAllNotificationsMutation.mutate,
    deleteAllNotificationsAsync: deleteAllNotificationsMutation.mutateAsync,
    isDeletingAllNotifications: deleteAllNotificationsMutation.isPending,
    deleteAllNotificationsError: deleteAllNotificationsMutation.error,
    deleteAllNotificationsSuccess: deleteAllNotificationsMutation.isSuccess,
    resetDeleteAllNotifications: deleteAllNotificationsMutation.reset,
  };
};
